import React, { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { motion, useReducedMotion } from 'framer-motion';
import { Provider } from 'react-redux';
import { Navigate, Route, Routes } from 'react-router-dom';
import { Toaster } from 'sonner';
import './App.css';
import Drawer from './Components/Drawer/Drawer';
import { useAppDispatch } from './redux/hooks';
import { fetchTasks } from './redux/tasks';
import { store } from './redux/store';
import BrainDump from './Routes/BrainDump/BrainDump';
import Dashboard from './Routes/Dashboard/Dashboard';
import HabitsScreen from './Routes/Habits/Habits';
import MoodScreen from './Routes/Mood/Mood';
import TaskDetails from './Routes/TaskDetails/TaskDetails';
import WorkspaceScreen from './Routes/Workspace/Workspace';
import { fetchHabits } from './redux/habits';

const MOBILE_NAV_QUERY = '(max-width: 900px)';

const TasksBootstrap = () => {
  const dispatch = useAppDispatch();
  useEffect(() => {
    void dispatch(fetchTasks());
  }, [dispatch]);
  return null;
};

const HabitsBootstrap = () => {
  const dispatch = useAppDispatch();
  useEffect(() => {
    void dispatch(fetchHabits());
  }, [dispatch]);
  return null;
};

function App() {
  const { isLoading, isAuthenticated, loginWithRedirect, logout } = useAuth0();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [chatPanelOpen, setChatPanelOpen] = useState(false);
  const reduceMotion = useReducedMotion();

  const handleLogout = () =>
    logout({ logoutParams: { returnTo: window.location.origin } });

  useEffect(() => {
    const mq = window.matchMedia(MOBILE_NAV_QUERY);
    const onChange = () => {
      if (mq.matches === false) setMobileNavOpen(false);
    };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  if (isLoading) return <div className="authLoading">Loading…</div>;

  if (!isAuthenticated) {
    return (
      <div className="authScreen">
        <img
          className="authScreenLogo"
          src={`${process.env.PUBLIC_URL}/img/lios-logo.svg`}
          alt="Lios"
          decoding="async"
        />
        <div className="authScreenActions">
          <button type="button" className="authButton" onClick={() => loginWithRedirect()}>
            Log in
          </button>
        </div>
      </div>
    );
  }

  return (
    <Provider store={store}>
      <Toaster
        position="top-right"
        richColors
        closeButton
        expand
        duration={3200}
        style={{ zIndex: 2147483647 }}
        toastOptions={{
          classNames: {
            toast: 'lios-toast',
            title: 'lios-toast-title',
            description: 'lios-toast-description',
            success: 'lios-toast-success',
            error: 'lios-toast-error',
          },
        }}
      />
      <TasksBootstrap />
      <HabitsBootstrap />
      <div className="wrapper">
        {mobileNavOpen ? (
          <button
            type="button"
            className="drawerBackdrop"
            aria-label="Close menu"
            onClick={() => setMobileNavOpen(false)}
          />
        ) : null}
        <button
          type="button"
          className="mobileNavToggle"
          aria-expanded={mobileNavOpen}
          aria-controls="app-drawer"
          onClick={() => setMobileNavOpen((open) => !open)}
        >
          <span className="mobileNavToggleBar" aria-hidden />
          <span className="mobileNavToggleBar" aria-hidden />
          <span className="mobileNavToggleBar" aria-hidden />
          <span className="visuallyHidden">
            {mobileNavOpen ? 'Close navigation menu' : 'Open navigation menu'}
          </span>
        </button>
        <section
          id="app-drawer"
          className={mobileNavOpen ? 'drawer drawerOpen' : 'drawer'}
        >
          <Drawer
            onNavigate={() => setMobileNavOpen(false)}
            onLogout={handleLogout}
          />
        </section>
        <section className="main">
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/tasks/:id" element={<TaskDetails />} />
            <Route path="/brain-dump" element={<BrainDump />} />
            <Route path="/habits" element={<HabitsScreen />} />
            <Route path="/mood" element={<MoodScreen />} />
            <Route path="/workspace" element={<WorkspaceScreen />} />
            <Route path="/workspace/:documentId" element={<WorkspaceScreen />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </section>
        <div className="chatColumn">
          <section
            id="app-chat-panel"
            className={chatPanelOpen ? 'chat' : 'chat chatCollapsed'}
            aria-hidden={!chatPanelOpen}
            aria-label="AI chat"
          />
        </div>
        <motion.button
          type="button"
          className={`chatFab${chatPanelOpen ? ' chatFabChatOpen' : ''}`}
          onClick={() => setChatPanelOpen((open) => !open)}
          aria-expanded={chatPanelOpen}
          aria-controls="app-chat-panel"
          title={chatPanelOpen ? 'Hide chat' : 'Show chat'}
          whileHover={reduceMotion ? undefined : { scale: 1.05 }}
          whileTap={reduceMotion ? undefined : { scale: 0.96 }}
          transition={{ type: 'spring', stiffness: 400, damping: 24 }}
        >
          <img
            className="chatFabIcon"
            src={`${process.env.PUBLIC_URL}/img/brain-ai.svg`}
            alt=""
            width={28}
            height={28}
            decoding="async"
          />
          <span className="visuallyHidden">
            {chatPanelOpen ? 'Hide chat panel.' : 'Show chat panel.'}
          </span>
        </motion.button>
      </div>
    </Provider>
  );
}

export default App;
